import { IntegrationAccess } from "./IntegrationAccess";
import { useRef, useEffect, useState } from "react";
import {
  Braces,
  Download,
  Upload,
  Code2,
  CheckCircle2,
  ArrowLeftRight,
  ExternalLink,
  Copy,
  Box,
} from "lucide-react";
import { parseProject, type Project, type Values } from "@proof/core";
import {
  assertDocumentData,
  fromLegacy,
  toLegacy,
  type LegacyEnvelope,
} from "@proof/legacy";
import {
  registerProofForm,
  registerProofAuthoring,
  type ProofAuthoringElement,
  type ProofFormElement,
  integrationOptionsSchema,
  authoringModes,
  type IntegrationOptions,
  type AuthoringMode,
  registerProofIntegrationModal,
  integrationModalOptionsSchema,
  type ProofIntegrationModalElement,
} from "@proof/sdk";
import {
  IntegrationControls,
  authoringModeLabels,
} from "./IntegrationControls";
import { Button, Control, JsonEditor } from "./components";
import { JsonImportButton } from "@proof/authoring/json-import";
import { downloadJson } from "./api";
import { useWorkspaceTransport } from "./WorkspaceContext";
import { OrderHandoff } from "./OrderHandoff";
import { ApiReference } from "./ApiReference";
import { ProjectPackage } from "./ProjectPackage";
registerProofForm();
registerProofAuthoring();
registerProofIntegrationModal();
declare module "react" {
  namespace JSX {
    interface IntrinsicElements {
      "proof-authoring": React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      > & { "api-url"?: string; mode?: string };
      "proof-integration-modal": React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      >;
      "proof-form": React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      > & {
        "show-proof"?: string;
        presentation?: string;
        width?: string;
        height?: string;
        "max-width"?: string;
        "max-height"?: string;
        "form-title"?: string;
        "trigger-label"?: string;
      };
    }
  }
}
import { useEsmAccess } from "./FeatureAccess";
export function Integrations({
  project,
  onImport,
  notify,
  scope,
  onAccess,
}: {
  project: Project;
  onImport: (projects: Project[]) => void | Promise<void>;
  notify: (s: string) => void;
  /** Project/version refinement shown under the heading (shared library). */
  scope?: React.ReactNode;
  onAccess?: () => void;
}) {
  const esmAllowed = useEsmAccess();
  const transport = useWorkspaceTransport();
  const [authenticatedEmbed, setAuthenticatedEmbed] = useState(!transport.demo);
  const optionsKey = `proof-integration:${encodeURIComponent(project.tenantId)}:${encodeURIComponent(project.id)}`;
  const [integration, setIntegration] = useState(() => {
    try {
      return integrationOptionsSchema.parse(
        JSON.parse(localStorage.getItem(optionsKey) || "{}"),
      );
    } catch {
      return integrationOptionsSchema.parse({});
    }
  });
  const [authoringMode, setAuthoringMode] = useState<AuthoringMode>("designer");
  const [popoutInitial] = useState(() => {
    try {
      const saved = JSON.parse(
        localStorage.getItem(optionsKey + ":popout") || "null",
      );
      if (saved && ["form", ...authoringModes].includes(saved.target))
        return {
          target: saved.target as "form" | AuthoringMode,
          configuration: integrationModalOptionsSchema.parse(
            saved.configuration,
          ),
        };
    } catch {}
    return {
      target: "configuration" as "form" | AuthoringMode,
      configuration: integrationModalOptionsSchema.parse({
        title: "Edit project variables",
        triggerLabel: "Edit variables",
      }),
    };
  });
  const [popoutTarget, setPopoutTarget] = useState<"form" | AuthoringMode>(
    popoutInitial.target,
  );
  const [popoutOptions, setPopoutOptions] = useState(
    popoutInitial.configuration,
  );
  const popoutIsForm = popoutTarget === "form";
  const popup = useRef<ProofIntegrationModalElement>(null);
  const changeIntegration = (next: IntegrationOptions) =>
    setIntegration(integrationOptionsSchema.parse(next));
  const rememberIntegration = () => {
    try {
      localStorage.setItem(optionsKey, JSON.stringify(integration));
      if (view === "popout")
        localStorage.setItem(
          optionsKey + ":popout",
          JSON.stringify({
            target: popoutTarget,
            configuration: integrationModalOptionsSchema.parse(popoutOptions),
          }),
        );
      notify("Integration settings remembered in this browser.");
    } catch {
      notify(
        "Browser storage is unavailable. Copy the setup code to keep your settings.",
      );
    }
  };
  const [packageOpen, setPackageOpen] = useState(false);
  const [view, setView] = useState(esmAllowed ? "adapter" : "embed"),
    [warnings, setWarnings] = useState<string[]>([]),
    [events, setEvents] = useState<string[]>([]);
  const component = useRef<ProofFormElement>(null);
  const [embedOptions, setEmbedOptions] = useState({
    presentation: "inline",
    width: "100%",
    height: "auto",
    maxWidth: "100%",
    maxHeight: "auto",
    title: "Customize document",
    triggerLabel: "Open form",
    showProof: true,
  });
  const [embedApi, setEmbedApi] = useState(() => {
    const url = new URL(window.location.origin);
    if (url.port === "5175") url.port = "4175";
    return url.origin;
  });
  const authoring = useRef<ProofAuthoringElement>(null);
  useEffect(() => {
    if (component.current)
      component.current.options = {
        ...integration,
        permissions: {
          ...integration.permissions,
          edit:
            integration.permissions.edit && transport.can("tenant.proofs.use"),
          proof:
            integration.permissions.proof && transport.can("tenant.proofs.use"),
        },
      };
    if (authoring.current)
      authoring.current.options = {
        ...integration,
        permissions: {
          edit:
            integration.permissions.edit &&
            transport.can("tenant.documents.manage"),
          proof:
            integration.permissions.proof && transport.can("tenant.proofs.use"),
          export:
            integration.permissions.export &&
            transport.can("tenant.documents.manage") &&
            transport.can("tenant.assets.manage"),
        },
      };
  }, [integration, view, transport, popoutIsForm]);
  useEffect(() => {
    const element = authoring.current;
    if (!element) return;
    element.headers = { ...transport.headers };
    element.services = transport.authoring;
    element.project = project;
    const listener = (e: Event) =>
      setEvents((current) =>
        [
          JSON.stringify({
            event: e.type,
            reason: (e as CustomEvent).detail.reason,
            projectId: (e as CustomEvent).detail.project.id,
          }),
          ...current,
        ].slice(0, 8),
      );
    element.addEventListener("proof:change", listener);
    return () => element.removeEventListener("proof:change", listener);
  }, [project, view, transport, popoutIsForm]);
  useEffect(() => {
    const element = component.current;
    if (!element) return;
    element.headers = { ...transport.headers };
    element.loadFont = transport.loadFont;
    element.images = {
      loadAsset: transport.loadAsset,
      upload: transport.upload,
      transform: transport.proofServices.transformImage,
    };
    element.project = project;
    const listener = (e: Event) =>
      setEvents((current) =>
        [
          JSON.stringify(
            { event: e.type, detail: (e as CustomEvent).detail },
            null,
            2,
          ),
          ...current,
        ].slice(0, 8),
      );
    element.addEventListener("proof:change", listener);
    return () => element.removeEventListener("proof:change", listener);
  }, [project, view, transport, popoutIsForm]);
  useEffect(() => {
    const modal = popup.current;
    if (!modal) return;
    const parsed = integrationModalOptionsSchema.safeParse(popoutOptions);
    if (parsed.success) modal.configuration = parsed.data;
    const listener = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      setPopoutOptions(
        integrationModalOptionsSchema.parse(detail.configuration),
      );
      if (!popoutIsForm && authoringModes.includes(detail.mode))
        setPopoutTarget(detail.mode);
      if (popoutIsForm)
        setEmbedOptions((old) => ({ ...old, showProof: detail.showProof }));
    };
    modal.addEventListener("proof:configuration-change", listener);
    return () =>
      modal.removeEventListener("proof:configuration-change", listener);
  }, [view, popoutOptions, popoutIsForm]);
  const readProjects = (value: unknown) => {
    assertDocumentData(value as never);
    const data = value as { schemaVersion?: unknown };
    if (data && typeof data === "object" && data.schemaVersion)
      return { projects: [parseProject(value)], warnings: [], legacy: false };
    const result = fromLegacy(value as never);
    if (!result.projects.length)
      throw new Error(
        "No projects were found in this file. Choose a valid project JSON export.",
      );
    return { ...result, legacy: true };
  };
  const attribute = (value: string) =>
    value
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  const optionsJson = JSON.stringify(integration, null, 2).replace(
    /</g,
    "\\u003c",
  );
  const basicAuthoringEmbed = `<script src="${attribute(embedApi.replace(/\/$/, ""))}/embed.js"></script>\n<proof-authoring mode="${authoringMode}"></proof-authoring>\n<script>\n  const editor = document.querySelector('proof-authoring');\n  editor.options = ${optionsJson};\n  editor.project = yourDocument;\n  // Provide authenticated host services before accepting changes.\n  editor.addEventListener('proof:change', e => saveDraft(e.detail.project));\n</script>`;
  const basicEmbed = `<script src="${attribute(embedApi.replace(/\/$/, ""))}/embed.js"></script>\n<proof-form project-id="${attribute(project.id)}"\n  api-url="${attribute(embedApi.replace(/\/$/, ""))}"\n  presentation="${embedOptions.presentation}"\n  width="${attribute(embedOptions.width)}" height="${attribute(embedOptions.height)}"\n  max-width="${attribute(embedOptions.maxWidth)}" max-height="${attribute(embedOptions.maxHeight)}"\n  form-title="${attribute(embedOptions.title)}"\n  trigger-label="${attribute(embedOptions.triggerLabel)}"${embedOptions.showProof ? " show-proof" : ""}>\n</proof-form>\n<script>\n  document.querySelector('proof-form').options = ${optionsJson};\n  document.querySelector('proof-form')\n    .addEventListener('proof:change', event => {\n      console.log(event.detail.values);\n    });\n</script>`;
  const scriptString = (value: string) =>
    JSON.stringify(value).replace(/</g, "\\u003c");
  const tokenEmbed = (
    tag: "proof-form" | "proof-authoring",
    popout = false,
  ) => `<script src="${attribute(embedApi.replace(/\/$/, ""))}/embed.js"></script>
<div id="proof-integration-host"></div>
<script>
(async () => {
  // Implement this endpoint on YOUR backend. Authenticate your customer there.
  // Your client secret must never appear in browser code.
  const response = await fetch('/your-server/proof-token', {
    method: 'POST', credentials: 'same-origin'
  });
  if (!response.ok) throw new Error('Access token unavailable');
  const { access_token } = await response.json();
  const element = document.createElement('${tag}');
  element.headers = { Authorization: 'Bearer ' + access_token };
  element.setAttribute('api-url', ${scriptString(embedApi.replace(/\/$/, ""))});
  element.setAttribute('project-id', ${scriptString(project.id)});
  ${
    tag === "proof-authoring"
      ? `element.setAttribute('mode', '${popout ? popoutTarget : authoringMode}');`
      : popout
        ? embedOptions.showProof
          ? "element.setAttribute('show-proof', '');"
          : ""
        : `element.setAttribute('presentation', '${embedOptions.presentation}');
  element.setAttribute('width', ${scriptString(embedOptions.width)});
  element.setAttribute('height', ${scriptString(embedOptions.height)});
  element.setAttribute('max-width', ${scriptString(embedOptions.maxWidth)});
  element.setAttribute('max-height', ${scriptString(embedOptions.maxHeight)});
  element.setAttribute('form-title', ${scriptString(embedOptions.title)});
  element.setAttribute('trigger-label', ${scriptString(embedOptions.triggerLabel)});${embedOptions.showProof ? "\n  element.setAttribute('show-proof', '');" : ""}`
  }
  element.options = ${optionsJson};
  ${popout ? `const popout = document.createElement('proof-integration-modal');\n  popout.configuration = ${JSON.stringify(popoutOptions).replace(/</g, "\\u003c")};\n  popout.append(element);\n  document.getElementById('proof-integration-host').append(popout);` : "document.getElementById('proof-integration-host').append(element);"}
})().catch(() => {
  document.getElementById('proof-integration-host').textContent = 'Unable to connect. Please sign in or try again.';
});
</script>`;
  const authoringEmbed = authenticatedEmbed
    ? tokenEmbed("proof-authoring")
    : basicAuthoringEmbed;
  const embed = authenticatedEmbed ? tokenEmbed("proof-form") : basicEmbed;
  const popoutValid = integrationModalOptionsSchema.safeParse(popoutOptions);
  const popoutTag = popoutIsForm ? "proof-form" : "proof-authoring";
  const popoutSnippet = authenticatedEmbed
    ? tokenEmbed(popoutTag, true)
    : `<script src="${attribute(embedApi.replace(/\/$/, ""))}/embed.js"></script>
<proof-integration-modal id="popout"><${popoutTag} id="popout-content"${popoutIsForm ? (embedOptions.showProof ? " show-proof" : "") : ` mode="${popoutTarget}"`}></${popoutTag}></proof-integration-modal>
<script>
  const popout = document.querySelector('#popout');
  popout.configuration = ${JSON.stringify(popoutOptions, null, 2).replace(/</g, "\\u003c")};
  const content = document.querySelector('#popout-content');
  content.options = ${optionsJson};
  content.project = yourDocument;
  content.addEventListener('proof:change', event => {
    // Persist event.detail.project or event.detail.values in your host.
  });
  popout.addEventListener('proof:configuration-change', event => {
    // Persist event.detail.configuration to retain window settings across reloads.
  });
  // Optional: popout.showModal(); popout.close();
</script>`;
  return (
    <div className="integrations-page">
      <div className="page-intro">
        <div>
          <span className="eyebrow">BUILT TO FIT YOUR SYSTEM</span>
          <h1>Integrations</h1>
          <p>Use the whole studio, or plug in just the pieces you need.</p>
        </div>
        <span className="badge green">
          <CheckCircle2 size={12} />
          Independent modules
        </span>
      </div>
      {scope}
      <div className="section-tabs">
        {[
          ["adapter", "Data transformer"],
          ["handoff", "Order handoff"],
          ["embed", "Embed a form"],
          ["authoring", "Embed authoring"],
          ["popout", "Popout integrations"],
          ["api", "API & packages"],
          ["schema", "Project JSON"],
        ]
          .filter(([id]) => esmAllowed || id !== "adapter")
          .map(([id, label]) => (
            <button
              key={id}
              className={view === id ? "active" : ""}
              onClick={() => setView(id)}
            >
              {label}
            </button>
          ))}
      </div>
      {view === "access" && <IntegrationAccess notify={notify} />}
      {["embed", "authoring", "popout"].includes(view) && (
        <section className="panel padded">
          <label>
            <input
              type="checkbox"
              checked={authenticatedEmbed}
              onChange={(event) => setAuthenticatedEmbed(event.target.checked)}
            />{" "}
            Use a B2B access token
          </label>
          <p>
            Your backend issues a short-lived token using its client
            credentials. The snippet sets authentication before the component
            connects. Register your website origin under Access &amp; tokens.
          </p>
          <Button onClick={() => (onAccess ? onAccess() : setView("access"))}>
            Configure access &amp; tokens
          </Button>
        </section>
      )}
      {view === "handoff" && (
        <OrderHandoff key={project.id} project={project} notify={notify} />
      )}
      {view === "adapter" && esmAllowed && (
        <>
          <section className="panel integration-hero">
            <div className="integration-icon">
              <ArrowLeftRight size={32} />
            </div>
            <h2>Your structure. Our engine.</h2>
            <p>
              Import the included ESM table structure, personalize with a
              canonical model, and transform it back. Unknown columns, unrelated
              tables, original XML, and legacy references stay in the envelope.
            </p>
            <div className="integration-flow">
              <span>ESM table JSON</span>
              <ArrowLeftRight size={20} />
              <span>Proof Studio model</span>
              <ArrowLeftRight size={20} />
              <span>ESM table JSON + layout sidecar</span>
            </div>
            <div className="integration-actions">
              <JsonImportButton
                kind="primary"
                label="Import project or ESM JSON"
                icon={<Upload size={15} aria-hidden />}
                title="Import a project"
                description={
                  <p>
                    Choose a Proof Studio project JSON (it has a
                    “schemaVersion”) or an ESM table export. Unknown columns and
                    tables are kept so they can be exported again.
                  </p>
                }
                maxBytes={12_000_000}
                sample={{
                  fileName: "example-esm-tables.json",
                  value: toLegacy([project]),
                }}
                inspect={(value) => {
                  const result = readProjects(value);
                  const first = result.projects[0];
                  return {
                    title:
                      result.projects.length === 1
                        ? first.name
                        : `${result.projects.length} projects`,
                    summary: [
                      {
                        label: "Format",
                        value: result.legacy
                          ? "ESM tables"
                          : "Proof Studio project",
                      },
                      {
                        label:
                          result.projects.length === 1 ? "Pages" : "Projects",
                        value: String(
                          result.projects.length === 1
                            ? first.pages.length
                            : result.projects.length,
                        ),
                      },
                      {
                        label: "Fields",
                        value: String(
                          result.projects.reduce(
                            (n, p) => n + p.fields.length,
                            0,
                          ),
                        ),
                      },
                    ],
                    warnings: result.warnings,
                    confirmLabel:
                      result.projects.length === 1
                        ? "Import project"
                        : "Import projects",
                  };
                }}
                onConfirm={async (value) => {
                  const result = readProjects(value);
                  await onImport(result.projects);
                  setWarnings(result.warnings);
                  notify(
                    "Project imported. Original unmapped data is retained for export.",
                  );
                }}
                finish="close"
              />
              <Button
                onClick={() => {
                  try {
                    downloadJson(
                      toLegacy([project]),
                      `${project.id}-legacy.json`,
                    );
                  } catch (e) {
                    notify((e as Error).message);
                  }
                }}
              >
                <Download size={15} />
                Export to legacy structure
              </Button>
              <Button
                onClick={() =>
                  downloadJson(project, `${project.id}-project.json`)
                }
              >
                <Braces size={15} />
                Export native project
              </Button>
              <Button onClick={() => setPackageOpen(true)}>
                <Box size={15} />
                Portable project package
              </Button>
            </div>
            {packageOpen && (
              <ProjectPackage
                project={project}
                onClose={() => setPackageOpen(false)}
              />
            )}
          </section>
          <div className="integration-detail-grid">
            <section className="panel padded">
              <h3>Sparse part overrides</h3>
              <p>
                Part definitions store only explicit overrides. Unconfigured
                variables resolve from the project. The SQL transformer emits
                part rows only for overridden variables.
              </p>
              <pre>
                {JSON.stringify(project.parts[0]?.overrides || {}, null, 2)}
              </pre>
            </section>
            <section className="panel padded">
              <h3>Portable visual layouts</h3>
              <p>
                Portable project packages carry editable native layouts,
                variables, rules and managed image/font files. Import the ZIP
                into another Proof Studio instance to create a separate draft.
                Editable IDML is available as a preview with a conversion
                report. Publishing-project exports remain under development.
              </p>
              <p>
                Export is a reviewable table envelope. It does not execute SQL
                or insert business records into a database.
              </p>
            </section>
          </div>
          {warnings.length > 0 && (
            <section className="panel padded">
              <h3>Import diagnostics</h3>
              {warnings.map((w, i) => (
                <p key={i}>{w}</p>
              ))}
            </section>
          )}
        </>
      )}
      {["embed", "authoring", "popout"].includes(view) && (
        <IntegrationControls
          options={integration}
          onChange={changeIntegration}
          authoring={
            view === "authoring" || (view === "popout" && !popoutIsForm)
          }
          onRemember={rememberIntegration}
        />
      )}
      {view === "popout" && (
        <>
          <section
            className="panel padded"
            aria-label="Popout integration setup"
          >
            <h2>Popout integrations</h2>
            <p>
              Open a project editor, its fields and variables, or a proof form
              in a configurable modal. Closing and reopening retains the mounted
              component; save project changes and values through your host to
              keep them across page reloads.
            </p>
            <div className="control-grid">
              <Control label="Popout integration type">
                <select
                  value={popoutTarget}
                  onChange={(e) => {
                    const target = e.target.value as typeof popoutTarget;
                    setPopoutTarget(target);
                    setPopoutOptions((value) => ({
                      ...value,
                      title:
                        target === "form"
                          ? "Customize document"
                          : authoringModeLabels[target],
                      triggerLabel:
                        target === "form"
                          ? "Open proof form"
                          : `Open ${authoringModeLabels[target].toLowerCase()}`,
                      width: target === "form" ? "720px" : "1120px",
                    }));
                  }}
                >
                  <option value="form">Proof form</option>
                  {authoringModes.map((mode) => (
                    <option
                      key={mode}
                      value={mode}
                      disabled={!integration.allowedModes.includes(mode)}
                    >
                      {authoringModeLabels[mode]}
                    </option>
                  ))}
                </select>
              </Control>
              {(
                [
                  ["title", "Modal title"],
                  ["triggerLabel", "Modal launch label"],
                  ["width", "Modal width"],
                  ["height", "Modal height"],
                  ["maxWidth", "Modal maximum width"],
                  ["maxHeight", "Modal maximum height"],
                ] as const
              ).map(([key, label]) => (
                <Control key={key} label={label}>
                  <input
                    value={popoutOptions[key]}
                    onChange={(e) =>
                      setPopoutOptions((value) => ({
                        ...value,
                        [key]: e.target.value,
                      }))
                    }
                  />
                </Control>
              ))}
              <Control label="Modal API base URL (snippet)">
                <input
                  value={embedApi}
                  onChange={(e) => setEmbedApi(e.target.value)}
                />
              </Control>
            </div>
            <label>
              <input
                type="checkbox"
                checked={popoutOptions.configurable}
                onChange={(e) =>
                  setPopoutOptions((value) => ({
                    ...value,
                    configurable: e.target.checked,
                  }))
                }
              />{" "}
              Allow configuration inside the popout
            </label>
            {popoutIsForm && (
              <label>
                <input
                  type="checkbox"
                  checked={embedOptions.showProof}
                  onChange={(e) =>
                    setEmbedOptions((value) => ({
                      ...value,
                      showProof: e.target.checked,
                    }))
                  }
                />{" "}
                Show document preview in popout
              </label>
            )}
            {!popoutValid.success && (
              <p role="alert">{popoutValid.error.issues[0].message}</p>
            )}
            <pre>
              {popoutValid.success
                ? popoutSnippet
                : "Enter valid popout settings to generate the snippet."}
            </pre>
            <Button
              disabled={!popoutValid.success}
              onClick={() =>
                void navigator.clipboard
                  .writeText(popoutSnippet)
                  .then(() => notify("Popout integration snippet copied."))
              }
            >
              <Copy size={14} /> Copy popout snippet
            </Button>
          </section>
          <section
            className="panel padded"
            aria-label="Live popout integration"
          >
            <h3>Live popout preview</h3>
            <proof-integration-modal ref={popup}>
              {popoutIsForm ? (
                <proof-form
                  ref={component}
                  show-proof={embedOptions.showProof ? "" : undefined}
                />
              ) : (
                <proof-authoring ref={authoring} mode={popoutTarget} />
              )}
            </proof-integration-modal>
          </section>
        </>
      )}
      {view === "embed" && (
        <div className="embed-grid">
          <section className="panel padded">
            <h3>Web Component</h3>
            <p className="muted">
              Works in plain HTML, React, Angular, Vue, and other hosts.
            </p>
            <div className="embed-options">
              <Control label="Form presentation">
                <select
                  value={embedOptions.presentation}
                  onChange={(e) =>
                    setEmbedOptions((current) => ({
                      ...current,
                      presentation: e.target.value,
                      width: e.target.value === "modal" ? "720px" : "100%",
                    }))
                  }
                >
                  <option value="inline">Inline</option>
                  <option value="modal">Modal</option>
                </select>
              </Control>
              {(
                [
                  ["width", "Form width"],
                  ["height", "Form height"],
                  ["maxWidth", "Maximum width"],
                  ["maxHeight", "Maximum height"],
                  ["title", "Form title"],
                  ["triggerLabel", "Launch button label"],
                ] as const
              ).map(([key, label]) => (
                <Control key={key} label={label}>
                  <input
                    value={embedOptions[key]}
                    onChange={(e) =>
                      setEmbedOptions((current) => ({
                        ...current,
                        [key]: e.target.value,
                      }))
                    }
                  />
                </Control>
              ))}
              <Control label="API base URL (snippet)">
                <input
                  type="url"
                  value={embedApi}
                  onChange={(e) => setEmbedApi(e.target.value)}
                />
              </Control>
            </div>
            <label className="embed-preview-toggle">
              <input
                type="checkbox"
                checked={embedOptions.showProof}
                onChange={(e) =>
                  setEmbedOptions((current) => ({
                    ...current,
                    showProof: e.target.checked,
                  }))
                }
              />{" "}
              Show document preview
            </label>
            <p className="muted">
              Sizes accept pixels (640 or 640px), %, rem, em, viewport units, or
              auto. The form scrolls within a fixed height; modals fit the
              viewport. Title and launch label apply to modal mode.
            </p>
            <p className="muted">
              The preview uses the current draft. The snippet loads the document
              stored by your API; use your deployed API address when embedding
              on another system.
            </p>
            <pre>{embed}</pre>
            <Button
              onClick={() =>
                void navigator.clipboard
                  .writeText(embed)
                  .then(() => notify("Embed snippet copied."))
              }
            >
              <Copy size={14} />
              Copy snippet
            </Button>
            <h3>Lifecycle events</h3>
            <p>
              <code>proof:ready</code> · <code>proof:change</code> ·{" "}
              <code>proof:error</code>
            </p>
            <div className="event-log">
              <span className="eyebrow">LIVE EVENTS</span>
              {events.length ? (
                events.map((e, i) => <pre key={i}>{e}</pre>)
              ) : (
                <p className="muted">
                  Change a field in the embedded form to see an event.
                </p>
              )}
            </div>
          </section>
          <section className="panel padded">
            <span className="badge green">Live embedded component</span>
            <proof-form
              ref={component}
              presentation={embedOptions.presentation}
              width={embedOptions.width}
              height={embedOptions.height}
              max-width={embedOptions.maxWidth}
              max-height={embedOptions.maxHeight}
              form-title={embedOptions.title}
              trigger-label={embedOptions.triggerLabel}
              show-proof={embedOptions.showProof ? "" : undefined}
            />
          </section>
        </div>
      )}
      {view === "authoring" && (
        <>
          <section className="panel padded">
            <h3>Independent authoring</h3>
            <p>
              Configuration, design and output settings share one
              host-controlled document. This sandbox emits changes without
              saving the workspace draft.
            </p>
            <Control label="Authoring mode">
              <select
                value={authoringMode}
                onChange={(e) =>
                  setAuthoringMode(e.target.value as AuthoringMode)
                }
              >
                {authoringModes.map((mode) => (
                  <option
                    key={mode}
                    value={mode}
                    disabled={!integration.allowedModes.includes(mode)}
                  >
                    {authoringModeLabels[mode]}
                  </option>
                ))}
              </select>
            </Control>
            <Control label="Authoring API base URL (snippet)">
              <input
                type="url"
                value={embedApi}
                onChange={(e) => setEmbedApi(e.target.value)}
              />
            </Control>
            <pre>{authoringEmbed}</pre>
            <Button
              onClick={() =>
                void navigator.clipboard
                  .writeText(authoringEmbed)
                  .then(() => notify("Authoring snippet copied."))
              }
            >
              <Copy size={14} /> Copy authoring snippet
            </Button>
          </section>
          <proof-authoring ref={authoring} api-url="" mode={authoringMode} />
          <section className="panel padded">
            <h3>Independent revision review</h3>
            <p>
              Match imported items, choose changes and preview a merged document
              in your own interface. Supply resource checks and preview loaders;
              accept and save the merged draft in your host.
            </p>
            <pre>{`<proof-revision-review id="review"></proof-revision-review>
<script>
  const review = document.querySelector('#review');
  review.services = yourResourceAndPreviewServices;
  review.onApply = async (project, report, signal) => {
    await saveWithRevisionCheck(project, currentDocument.revision, signal);
  };
  review.project = currentDocument;
  review.baseline = draftWhenImportStarted; // optional
  review.incoming = importedDocument;
  review.importReport = importReport; // optional, after incoming
</script>`}</pre>
          </section>
          <section className="panel padded">
            <h3>Independent managed font library</h3>
            <p>
              Use the same font catalog, previews, uploads, downloads and
              text-style creation in your host. Supply managed-byte services or
              opt into an explicit API connection.
            </p>
            <pre>{`<proof-font-library id="fonts"></proof-font-library>
<script>
  const fonts = document.querySelector('#fonts');
  fonts.project = yourDocument;
  fonts.services = yourFontStorageCallbacks;
  fonts.addEventListener('proof:change', e => saveDraft(e.detail.project));
  fonts.addEventListener('proof:font', e => {
    e.preventDefault();
    deliverFont(e.detail.blob, e.detail.filename);
  });
</script>`}</pre>
          </section>
        </>
      )}
      {view === "api" && (
        <>
          <ApiReference notify={notify} />
          <div className="module-grid">
            {[
              [
                "@proof/core",
                "Schemas, configuration, rules, validation, scene geometry, repository contracts.",
              ],
              [
                "@proof/contracts",
                "Portable OpenAPI definitions for the native tenant API and independent host clients.",
              ],
              [
                "@proof/sdk",
                "HTTP client and native form/authoring Web Components for other systems.",
              ],
              [
                "@proof/importer",
                "PDF, INDD, IDML and image import with generated variables and document frames.",
              ],
              [
                "@proof/jobs",
                "Immutable generation, durable render attempts and aggregate bulk workflows.",
              ],
              [
                "@proof/handoff",
                "Approved PDF/customization/XVP package retention with independent storage.",
              ],
              [
                "@proof/profiles",
                "Personal reusable form profiles with owner scope, revisions and pluggable storage.",
              ],
              [
                "@proof/ui",
                "Independent React form generator and proof renderer.",
              ],
              [
                "@proof/authoring",
                "Host-controlled field configuration, document designer and output settings with injected services.",
              ],
              [
                "@proof/renderer",
                "Stateless PDF, SVG and high-resolution raster generation.",
              ],
              [
                "@proof/legacy",
                "Bidirectional ESM tables, customization and scoped global-profile snapshot exchange.",
              ],
              [
                "@proof/server",
                "Host identity boundary, API, assets, and render job orchestration.",
              ],
            ]
              .filter(([name]) => esmAllowed || name !== "@proof/legacy")
              .map(([name, desc]) => (
                <section className="panel padded" key={name}>
                  <Box size={22} />
                  <h3>{name}</h3>
                  <p>{desc}</p>
                </section>
              ))}
          </div>
          <section className="panel padded">
            <h3>Generate through REST</h3>
            <pre>{`POST /api/jobs\n{\n  "projectId": "${project.id}",\n  "versionId": "<immutable-published-version>",\n  "values": { "<field-id>": "Your value" },\n  "profileId": "${project.outputs[0].id}"\n}\n\nGET /api/jobs/:id\nGET /api/jobs/:id/artifact\nPOST /api/jobs/:id/cancel  { "reason": "..." }\nPOST /api/jobs/:id/retry   { "reason": "..." }`}</pre>
            <p>
              Demo mode also accepts a complete project snapshot and profile.
              Deployments resolve authoritative published versions and host
              identity.
            </p>
            <h3>Replace your persistence</h3>
            <pre>{`const { app } = createApp({\n  demo: false,\n  repository: hostRepository,\n  assets: hostAssetStore,\n  identity: async request => hostPrincipal(request),\n  revalidate: async actor => currentPrincipal(actor),\n  origins: ['https://your-host.example']\n});`}</pre>
          </section>
        </>
      )}
      {view === "schema" && (
        <section className="panel padded">
          <div className="section-heading">
            <h3>Canonical project definition</h3>
            <Button onClick={() => downloadJson(project, `${project.id}.json`)}>
              <Download size={14} />
              Download JSON
            </Button>
          </div>
          <JsonEditor
            value={project}
            onSave={(v) => onImport([parseProject(v)])}
            label="Native project JSON"
          />
        </section>
      )}
    </div>
  );
}
